import * as React from 'react';
import classnames from 'classnames';
import difference from 'lodash/difference';
import { connect } from 'react-redux';
import isNil from 'lodash/isNil';
import ReactDOMServer from 'react-dom/server';

import { PromptExit } from 'libs/router';
import InstagramDataEnabled from 'components/instagram-data-enabled/InstagramDataEnabled';

import SelectStorySnippets, {
  getStoriesForDeliverable,
  saveStorySnippets
} from 'modules/select-story-snippets';
import DeliverablePostUrl from './deliverable-post-url/DeliverablePostUrl';
import DeliverableAttachmentFeedbackModal from '../../deliverable-attachment-feedback-modal/DeliverableAttachmentFeedbackModal';
import StorySnippets from '../../story-snippets/StorySnippets';
import DeliverableInsights from './deliverable-insights/DeliverableInsights';
import ConnectYourInstagramChannel from './connect-your-instagram-channel/ConnectYourInstagramChannel';
import DeliverableSubHeader from '../deliverable-sub-header/DeliverableSubHeader';
import DeliverableTabFooter from '../deliverable-tab-footer/DeliverableTabFooter';
import StorySnippetsList from './story-snippets-list/StorySnippetsList';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import {
  downloadDeliverableAttachments,
  downloadStoryDeliverableAttachments
} from 'modules/deliverables/reducers/deliverableMediaAttachments';
import { updateInsights } from 'modules/subcampaigns/reducers/offerDeliverables';
import {
  updateInsightsData,
  updateStoryInsightsData
} from 'modules/deliverables/reducers/deliverableInsights';
import { updateDeliverableInsights } from 'modules/campaigns/reducers/campaignDeliverables';
import { reFetchCurrentAccountBasedOnSelectedOrg } from 'modules/access/reducers/account';

import { getDeliverableTrackingActionType } from 'modules/deliverables/utils/tracking-helper';
import { __t, applyTranslations } from 'utils/language';
import { getDisplayDateRange } from 'utils/date';
import { logError } from 'utils/log';
import {
  DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS,
  DELIVERABLE_ATTACHMENT_TYPE_HIGHRES,
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  isAnalyticsScreenshotRequired,
  isHighresImagesRequired
} from 'utils/deliverables';
import {
  isInstagramPostUrl,
  isInstagramReelUrl,
  isUrl,
  isValidTikTokVideoUrl,
  isYoutubeVideoUrl
} from 'utils/validators';
import { areThereInsightsWithoutSnippets } from './utils/story-validation';
import { prepareStoryDataForBE } from './utils/story-data-preparation';

import './deliverable-insights-tab.scss';

applyTranslations(require('./deliverable-insights-tab.i18n.json'));

import {
  Deliverable,
  DeliverableAttachment,
  DeliverableAttachmentForUpdate,
  DeliverableAttachmentType,
  DeliverablePost,
  DeliverableType,
  DeliverableUploadedEventAction,
  InfluencerProfile,
  LinkedDeliverableAttachment,
  Organization,
  StorySnippet,
  Subcampaign,
  SubcampaignInfluencerProfile
} from 'types';

type OwnProps = {
  deliverable: Deliverable,
  isForBrand?: boolean,
  postAttachments: Array<DeliverableAttachment>,
  subcampaign: Subcampaign,
  isContentPage?: boolean,
  setDeliverableModified: (isModified: boolean) => void,
  onTrackDeliverableUploadEvent: (eventAction: DeliverableUploadedEventAction) => void
}

type StateProps = {
  isStorySnippetFeatureEnabled: boolean,
  hasActiveFbConnection: (influencerId: number) => boolean
};

type DispatchProps = {
  dispatchFetchCurrentUserAccount: () => Promise<void>,
  dispatchDownloadAttachments: (
    campaignId: number,
    creatorProfile: SubcampaignInfluencerProfile | undefined | null,
    deliverable: Deliverable,
    attachmentType?: string | null,
    approvedOnly?: boolean | null
  ) => void,
  dispatchDownloadStoryDeliverableAttachments: (
    campaignId: number,
    deliverable: Deliverable,
    creatorProfile: SubcampaignInfluencerProfile | undefined | null
  ) => void,
  dispatchShowConfirmationModal: (
    params: {
      title?: string,
      message?: string,
      onCancel?: () => void,
      onClose?: () => void,
      innerContent?: React.ReactNode,
      confirmButtonLabel: string,
      onProceed?: () => void,
      onConfirm?: () => void,
      proceedButtonLabel?: string
    }
  ) => void,
  dispatchUpdateInsightsData: (
    offerId: number,
    deliverableId: number,
    addAttachments: Array<DeliverableAttachment>,
    deleteAttachments: Array<number>,
    postUrl: string | null,
    isUrlChanged: boolean
  ) => Promise<Deliverable>,
  dispatchHideConfirmationModal: () => void,
  dipatchUpdateDeliverable: (
    offerId: number,
    deliverable: Deliverable,
    post?: DeliverablePost,
    isContentPage?: boolean) => void,
  dispatchUpdateStoryInsightsData: (
    offerId: number,
    deliverableId: number,
    addAttachments: Array<DeliverableAttachment>,
    deleteAttachments: Array<number>,
    linkedAttachments: Array<LinkedDeliverableAttachment>,
    updateAttachments: Array<DeliverableAttachmentForUpdate>
  ) => Promise<Deliverable>
}

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  postUrlError: string,
  isEditMode: boolean,
  attachments: Array<DeliverableAttachment>,
  invalidAttachments: Array<DeliverableAttachment>,
  isModified: boolean,
  showConnectYourInstagram: boolean,
  originalStorySnippetCodes: Array<string>,
  storySnippets: Array<StorySnippet>,
  externallyDeletedSnippetAttachments: Array<number>,
  lastDiscardAt: number,
  validateStorySnippetsAt: null | number,
  postUrl?: string,
  post?: DeliverablePost
}

const VALIDATION_FN = {
  instagram_post: isInstagramPostUrl,
  instagram_reel: isInstagramReelUrl,
  tiktok_video: isValidTikTokVideoUrl,
  youtube_video: isYoutubeVideoUrl,
  other: isUrl
};

const msgKey = 'deliverable_insights_tab';

class DeliverableInsightsTab extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    const { deliverable, isForBrand, hasActiveFbConnection } = props;
    const { postAttachments, post, deliverableType } = deliverable;
    const analyticsProvided = (postAttachments ?? [])
      .some(a => a.meta?.type === 'analytics');
    const needIGConnection = [DELIVERABLE_TYPE_INSTAGRAM_POST, DELIVERABLE_TYPE_INSTAGRAM_STORY]
      .includes(deliverableType);
    const creatorId = deliverable.creatorId ?? deliverable?.creatorProfile?.id ?? -1;

    this.state = {
      postUrlError: '',
      isEditMode: !isForBrand,
      postUrl: post?.url,
      attachments: postAttachments || [],
      invalidAttachments: [],
      isModified: false,
      externallyDeletedSnippetAttachments: [],
      post: deliverable.post,
      showConnectYourInstagram: !isForBrand
        && !hasActiveFbConnection(creatorId) && !analyticsProvided && needIGConnection,
      storySnippets: [],
      originalStorySnippetCodes: [],
      lastDiscardAt: -1,
      validateStorySnippetsAt: null
    };
  }

  componentDidMount() {
    this.setDefaultStorySnippets();
  }

  async setDefaultStorySnippets() {
    try {
      const { deliverable, hasActiveFbConnection } = this.props;
      const creatorId = deliverable.creatorId ?? deliverable.creatorProfile?.id ?? -1;

      if (
        !hasActiveFbConnection(creatorId)
        || !deliverable.isFacebookDataEnabled
        || deliverable.deliverableType !== DELIVERABLE_TYPE_INSTAGRAM_STORY
      ) {
        return;
      }

      let storySnippets = await getStoriesForDeliverable(deliverable.id);
      storySnippets = storySnippets.filter(s => s.selected);
      const originalStorySnippetCodes = storySnippets.map(s => s.code);

      this.setState({ storySnippets, originalStorySnippetCodes });
    } catch (error) {
      logError(error);
    }
  }

  setEditMode = (isEditMode: boolean) => {
    this.setState(prevState => ({
      ...prevState,
      isEditMode
    }));
  };

  setAttachments = (newAttachments: Array<DeliverableAttachment>) => {
    const { attachments } = this.state;
    const shouldReplace = newAttachments
      .filter(att => attachments.map(a => a.id).includes(att.id));

    if (shouldReplace.length > 0) {
      this.setState(prev => ({
        ...prev,
        attachments: [
          ...prev.attachments.filter(att => !shouldReplace.map(a => a.id).includes(att.id)),
          ...shouldReplace
        ]
      }));
    } else {
      this.setState(prevState => ({
        ...prevState,
        attachments: [...prevState.attachments, ...newAttachments]
      }));
    }
  };

  isFbDataEnabled = () => {
    const {
      deliverable,
      hasActiveFbConnection
    } = this.props;

    const creatorId = deliverable.creatorId ?? deliverable?.creatorProfile?.id ?? -1;

    const isFbConnected = hasActiveFbConnection(creatorId);

    // If fb is not connected but data is already submitted using fb
    if (!isFbConnected && deliverable.isFacebookDataEnabled) {
      return !!deliverable.postAttachments?.length;
    }

    return deliverable.isFacebookDataEnabled &&
    isFbConnected;
  };

  handleOnTrackDeliverableUploadEvent(
    type: DeliverableType,
    content: 'url' | 'analytics' | 'high-res'
  ) {
    const { onTrackDeliverableUploadEvent } = this.props;
    const eventAction = getDeliverableTrackingActionType(type, content);

    if (eventAction === null) {
      return;
    }

    onTrackDeliverableUploadEvent(eventAction);
  }

  handleModification = (isDeliverableChanged: boolean, nextState: Partial<State>) => {
    const { setDeliverableModified } = this.props;
    this.setState(prevState => ({
      ...prevState,
      ...nextState,
      isModified: isDeliverableChanged
    }));
    setDeliverableModified(isDeliverableChanged);
  };

  showUploadAnywayModal = (
    attachmentType: DeliverableAttachmentType,
    onProceed: () => void,
    onTryAgain?: () => void

  ) => {
    const { dispatchShowConfirmationModal, dispatchHideConfirmationModal, deliverable } = this.props;
    const { deliverableType } = deliverable;

    dispatchShowConfirmationModal({
      innerContent: (
        <DeliverableAttachmentFeedbackModal
          deliverableType={deliverableType}
          attachmentType={attachmentType}
        />
      ),
      confirmButtonLabel: __t('deliverable_media_attachments.button_discard'),
      onConfirm: () => {
        dispatchHideConfirmationModal();

        if (onTryAgain) {
          onTryAgain();
        }
      },
      proceedButtonLabel: __t('deliverable_media_attachments.button_upload'),
      onProceed: () => {
        dispatchHideConfirmationModal();
        // attach invalid images anyway
        onProceed();
      }
    });
  };

  handleUpdateAttachments = (
    tempAttachments: Array<DeliverableAttachment & { isInvalidImage?: boolean }>,
    attachmentType: DeliverableAttachmentType
  ) => {
    const validAttachments = tempAttachments.filter(attachment => !attachment.isInvalidImage);
    const inValidAttachments = tempAttachments.filter(attachment => attachment.isInvalidImage);

    this.handleModification(true, { isModified: true });
    this.setAttachments(validAttachments);

    if (inValidAttachments.length) {
      this.setState(prev => ({
        ...prev,
        invalidAttachments: [...prev.invalidAttachments, ...inValidAttachments]
      }), () => {
        this.showUploadAnywayModal(
          attachmentType,
          () => {
            this.setAttachments(this.state.invalidAttachments);
            this.setState(prev => ({
              ...prev,
              invalidAttachments: []
            }));
          },
          () => {
            this.setState(prev => ({
              ...prev,
              externallyDeletedSnippetAttachments: inValidAttachments.map(att => att.id as number)
            }));
          });
      });
    }
  };

  handleDeleteAttachments = (deliverableAttachments: Array<DeliverableAttachment>) => {
    const {
      attachments,
      externallyDeletedSnippetAttachments
    } = this.state;

    const updatedAttachments = attachments.filter(
      attachment => !deliverableAttachments
        .find(deliverableAttachment => deliverableAttachment.id === attachment.id));

    this.handleModification(true, {
      externallyDeletedSnippetAttachments: [
        ...externallyDeletedSnippetAttachments,
        ...(
          updatedAttachments.length === 0
            ? attachments.map(att => att.id as number)
            : []
        )
      ],
      attachments: [...updatedAttachments],
      storySnippets: []
    });
  };

  handleReplaceAttachment = (
    replacement: DeliverableAttachment & { isInvalidImage?: boolean },
    current: DeliverableAttachment,
    attachmentType: DeliverableAttachmentType
  ) => {
    const replace = () => {
      const { attachments } = this.state;
      const updatedAttachments = attachments
        .map(attachment => (current.id === attachment.id ? replacement : attachment));

      this.handleModification(true, { attachments: [...updatedAttachments] });
    };

    if (replacement.isInvalidImage) {
      this.showUploadAnywayModal(attachmentType, replace);
    } else {
      replace();
    }
  };

  handlePostUrlChange = (url: string) => {
    this.handleModification(true, { postUrl: url, postUrlError: '', isModified: true });
  };

  handleDownloadAttachments = (approvedOnly?: boolean | null) => {
    const {
      subcampaign,
      deliverable,
      dispatchDownloadAttachments,
      dispatchDownloadStoryDeliverableAttachments
    } = this.props;
    const { campaignId, creatorProfile } = subcampaign;

    if (this.isStorySnippetsWidgetAvailable()) {
      dispatchDownloadStoryDeliverableAttachments(campaignId, deliverable, creatorProfile);

      return;
    }

    dispatchDownloadAttachments(campaignId, creatorProfile, deliverable, undefined, approvedOnly);
  };

  saveInsightsData = () => {
    const {
      isForBrand,
      isContentPage,
      subcampaign,
      deliverable,
      dispatchUpdateInsightsData,
      dipatchUpdateDeliverable,
      dispatchUpdateStoryInsightsData
    } = this.props;
    const { attachments, postUrl, post: statePost } = this.state;
    const { postAttachments: savedPostAttachments, post: savedPost, id } = deliverable;
    const { offer } = subcampaign;

    if (this.isStorySnippetsWidgetAvailable()) {
      const {
        addAttachments,
        deleteAttachments,
        linkedAttachments,
        updateAttachments
      } = prepareStoryDataForBE({
        prevAttachments: savedPostAttachments ?? [],
        nextAttachments: attachments
      });

      return dispatchUpdateStoryInsightsData(
        deliverable.offerId,
        deliverable.id,
        addAttachments,
        deleteAttachments,
        linkedAttachments,
        updateAttachments
      ).then(updatedDeliverable => {
        if (!deliverable || !updatedDeliverable) {
          return;
        }

        if (addAttachments.some(a => a.meta?.type === DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS)) {
          this.handleOnTrackDeliverableUploadEvent(deliverable.deliverableType, 'analytics');
        }

        if (addAttachments.some(a => a.meta?.type === DELIVERABLE_ATTACHMENT_TYPE_HIGHRES)) {
          this.handleOnTrackDeliverableUploadEvent(deliverable.deliverableType, 'high-res');
        }

        this.handleModification(false, {
          isEditMode: !isForBrand,
          attachments: updatedDeliverable?.postAttachments ?? [],
          validateStorySnippetsAt: null
        });

        dipatchUpdateDeliverable(
          offer.id,
          updatedDeliverable,
          updatedDeliverable.post || statePost,
          isContentPage
        );
      });
    }

    const addedAttachments = attachments.filter(attachment => !savedPostAttachments?.find(postAttachment => postAttachment.id === attachment.id));
    const deleteAttachments = (savedPostAttachments || [])
      .filter(postAttachment => !attachments.find(attachment => attachment.id === postAttachment.id))
      .map(postAttachment => postAttachment.id);

    const isUrlChanged = savedPost?.id && savedPost?.url !== postUrl;
    const updatedUrl = postUrl !== savedPost?.url ? postUrl : null;

    return dispatchUpdateInsightsData(
      offer.id,
      id,
      addedAttachments, // @ts-ignore deletedAttachments will always have ids as number, as its extracted from BE data
      deleteAttachments,
      updatedUrl,
      isUrlChanged
    ).then((response) => {
      if (!response) {
        return;
      }

      if (updatedUrl) {
        this.handleOnTrackDeliverableUploadEvent(deliverable.deliverableType, 'url');
      }

      if (addedAttachments.some(a => a.meta?.type === DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS)) {
        this.handleOnTrackDeliverableUploadEvent(deliverable.deliverableType, 'analytics');
      }

      if (addedAttachments.some(a => a.meta?.type === DELIVERABLE_ATTACHMENT_TYPE_HIGHRES)) {
        this.handleOnTrackDeliverableUploadEvent(deliverable.deliverableType, 'high-res');
      }

      this.handleModification(false, {
        externallyDeletedSnippetAttachments: [],
        isEditMode: !isForBrand,
        attachments: response.postAttachments || [],
        post: response.post || statePost,
        validateStorySnippetsAt: null
      });

      dipatchUpdateDeliverable(
        offer.id,
        response,
        response.post || statePost,
        isContentPage
      );
    }).then(this.saveStorySnippets);
  };

  saveStorySnippets = async () => {
    try {
      const { deliverable } = this.props;
      const { storySnippets, originalStorySnippetCodes } = this.state;
      const selectedStories = storySnippets.map(s => s.code);
      const removed = difference(originalStorySnippetCodes, selectedStories);
      const added = difference(selectedStories, originalStorySnippetCodes);

      if (added.length > 0 || removed.length > 0) {
        await saveStorySnippets(deliverable.id, added, removed);
      }
    } catch (error) {
      logError(error);
    }
  };

  validateStorySnippets() {
    const { attachments } = this.state;
    const isValid = !areThereInsightsWithoutSnippets(attachments);

    this.setState({
      validateStorySnippetsAt: !isValid ? Date.now() : null
    });

    return isValid;
  }

  shouldSave = () => {
    const { dispatchShowConfirmationModal, dispatchHideConfirmationModal } = this.props;
    const { isModified } = this.state;

    if (!isModified) {
      dispatchShowConfirmationModal({
        title: __t(`${msgKey}.no_changes_modal.title`),
        message: __t(`${msgKey}.no_changes_modal.message`),
        confirmButtonLabel: __t(`${msgKey}.no_changes_modal.confirm_btn`),
        onClose: () => dispatchHideConfirmationModal(),
        onConfirm: () => dispatchHideConfirmationModal()
      });

      return false;
    }

    return true;
  };

  handleSave = () => {
    if (!this.shouldSave()) {
      return;
    }

    const {
      deliverable,
      dispatchShowConfirmationModal,
      dispatchHideConfirmationModal
    } = this.props;
    const { postUrl, attachments, isModified } = this.state;
    const { deliverableType, postAttachments } = deliverable;

    if (this.isStorySnippetsWidgetAvailable() && !this.validateStorySnippets()) {
      return;
    }

    const validation = VALIDATION_FN[deliverableType]?.(postUrl);
    const isValid = validation?.valid ?? true; // treat as valid if validation is undefined

    if (!(postUrl || attachments.length || postAttachments?.length) && !isModified) {
      dispatchShowConfirmationModal({
        title: __t(`${msgKey}.add_content_modal.title`),
        message: __t(`${msgKey}.add_content_modal.message`),
        confirmButtonLabel: __t(`${msgKey}.add_content_modal.continue_btn`),
        onClose: () => dispatchHideConfirmationModal(),
        onConfirm: () => dispatchHideConfirmationModal()
      });
    } else if (postUrl && !isValid) {
      this.setState(prevState => ({
        ...prevState,
        postUrlError: validation?.message
      }));
    } else {
      this.saveInsightsData();
    }
  };

  handleCancelChanges = () => {
    const { isModified } = this.state;
    const { deliverable, isForBrand, dispatchHideConfirmationModal, dispatchShowConfirmationModal } = this.props;
    const { postAttachments: savedPostAttachments, post: savedPost } = deliverable;

    if (isModified) {
      dispatchShowConfirmationModal({
        title: __t(`${msgKey}.discard_changes_modal.title`),
        message: __t(`${msgKey}.discard_changes_modal.message`),
        confirmButtonLabel: __t(`${msgKey}.discard_changes_modal.discard_btn`),
        onClose: () => dispatchHideConfirmationModal(),
        onCancel: () => dispatchHideConfirmationModal(),
        onConfirm: async () => {
          await this.setDefaultStorySnippets();
          this.handleModification(false, {
            lastDiscardAt: Date.now(),
            attachments: savedPostAttachments || [],
            postUrl: savedPost?.url,
            isEditMode: !isForBrand,
            postUrlError: '',
            post: savedPost
          });

          dispatchHideConfirmationModal();
        },
        innerContent: (
          <div className="deliverable-insights-tab__cofirmation-message">
            {__t(`${msgKey}.discard_changes_modal.confirmation_message`)}
          </div>
        )
      });
    } else {
      this.setState(prevState => ({
        ...prevState,
        isEditMode: !isForBrand
      }));
    }
  };

  isStorySnippetsWidgetAvailable() {
    const {
      deliverable,
      isStorySnippetFeatureEnabled
    } = this.props;
    const hasFBData = this.isFbDataEnabled();
    const isDeliverableWithOldStories = (deliverable.postAttachments ?? []).length > 0
      && (deliverable.postAttachments ?? []).every(att => !att.canHaveRelatedAssets);

    return deliverable.deliverableType === DELIVERABLE_TYPE_INSTAGRAM_STORY
      && !isDeliverableWithOldStories && isStorySnippetFeatureEnabled && !hasFBData;
  }

  getStorySnippetsKey(): string {
    const { deliverable } = this.props;
    const { lastDiscardAt } = this.state;
    const urls = (deliverable.postAttachments ?? []).map(att => (
      new URL(att.url)?.pathname?.slice(1, 10) ?? ''
    )).join('|');
    const when = String(lastDiscardAt);

    return `${urls}|${when}`;
  }

  getSubheadline(): string {
    const { deliverable, isForBrand } = this.props;
    const { deliverableType, requiresApproval } = deliverable;
    const userType = isForBrand ? 'brand' : 'influencer';
    const approvalType = requiresApproval ? '' : '_without_approval';
    const storyType = this.isStorySnippetsWidgetAvailable() && !requiresApproval
      ? 'snippet_insights_subheadline'
      : 'insights_subheadline';
    const fb = deliverable.isFacebookDataEnabled ? '_fb' : '';

    return __t(`${msgKey}.headline.${userType}.${deliverableType}.${storyType}${approvalType}${fb}`);
  }

  render() {
    const {
      deliverable,
      isForBrand,
      subcampaign,
      dispatchFetchCurrentUserAccount
    } = this.props;
    const {
      attachments,
      isEditMode,
      postUrl,
      post,
      postUrlError,
      isModified,
      storySnippets,
      showConnectYourInstagram,
      externallyDeletedSnippetAttachments,
      validateStorySnippetsAt
    } = this.state;
    const { deliverableType, insightsProvisionTimeline, requiresApproval } = deliverable;

    const requiresHighResImages = isHighresImagesRequired(!!requiresApproval, deliverableType);
    const requiresAnalytics = isAnalyticsScreenshotRequired(deliverableType);
    const hasFBData = this.isFbDataEnabled();
    const isIGDeliverable = [DELIVERABLE_TYPE_INSTAGRAM_POST, DELIVERABLE_TYPE_INSTAGRAM_STORY, DELIVERABLE_TYPE_INSTAGRAM_REEL]
      .includes(deliverableType);
    const gridClass = classnames({
      'deliverable-insights-tab__grid': showConnectYourInstagram
    });
    const additionalTexts = {
      instagram_post: __t(`${msgKey}.instagram_enabled_additional_texts.post`),
      instagram_story: __t(`${msgKey}.instagram_enabled_additional_texts.story`),
      instagram_reel: __t(`${msgKey}.instagram_enabled_additional_texts.reel`)
    };
    const campaignStartDate = subcampaign.campaign.startDate;

    return (
      <div className="deliverable-insights-tab">
        {
          isModified && (
            <PromptExit
              type="page"
              message={__t(`${msgKey}.discard_changes_modal.info`)}
              title={__t(`${msgKey}.discard_changes_modal.title`)}
              innerContent={ReactDOMServer.renderToString(
                <div className="deliverable-insights-tab__modal-info">
                  {__t(`${msgKey}.discard_changes_modal.confirmation_message`)}
                </div>
              )}
            />
          )
        }
        <div className={gridClass}>
          {/* A hacky fix before we come up with a better solution. Approved by Kolianey */}
          <div style={{ marginTop: showConnectYourInstagram ? 90 : undefined }}>
            <div>
              <div className="deliverable-insights-tab__date-section">
                {`${__t(`${msgKey}.labels.insights_provision_date`)}:
                ${getDisplayDateRange(insightsProvisionTimeline.startDate, insightsProvisionTimeline.endDate)}`}
              </div>
              <div className="deliverable-insights-tab__subheadline">
                {this.getSubheadline()}
              </div>
            </div>
            {
              requiresAnalytics && hasFBData && isIGDeliverable && (
                <div className="deliverable-insights-tab__instagram-enabled">
                  <DeliverableSubHeader
                    title={__t(`${msgKey}.instagram_enabled_additional_texts.insights`)}
                    instruction={
                      __t(`${msgKey}.instagram_enabled_additional_texts.insights_description`)
                    }
                    directon="column"
                  />
                  <InstagramDataEnabled
                    hasActiveFbConnection
                    additionalText={{
                      text: additionalTexts[deliverableType],
                      hasUnderline: true
                    }}
                  />
                </div>
              )
            }
            {
              deliverableType !== DELIVERABLE_TYPE_INSTAGRAM_STORY && (
                <DeliverablePostUrl
                  isEditMode={isEditMode}
                  deliverable={deliverable}
                  deliverablePost={post}
                  postUrl={postUrl}
                  onSetPostUrl={this.handlePostUrlChange}
                  onSetEditMode={() => this.setEditMode(!isEditMode)}
                  postUrlError={postUrlError}
                  onDeleteUrl={() =>
                    this.handleModification(true, { post: undefined, postUrl: '', postUrlError: '' })
                  }
                  onEditUrl={() =>
                    this.handleModification(true, { post: undefined, postUrlError: '' })
                  }
                  isForBrand={isForBrand}
                />
              )
            }
            {
              requiresAnalytics && !hasFBData
              && deliverableType !== DELIVERABLE_TYPE_INSTAGRAM_STORY && (
                <DeliverableInsights
                  isForBrand={isForBrand}
                  isEditMode={isEditMode}
                  deliverable={deliverable}
                  onEditMode={this.setEditMode}
                  attachments={attachments.filter(
                    attachment => attachment.meta?.type === DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS
                  )}
                  onUpdate={tempAttachments => {
                    this.handleUpdateAttachments(tempAttachments, DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS);
                  }}
                  onDeleteAttachments={this.handleDeleteAttachments}
                  onReplaceAttachment={(replacement, current) => {
                    this.handleReplaceAttachment(replacement, current, DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS);
                  }}
                  mode={DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS}
                />
              )
            }
            {
              !hasFBData && requiresHighResImages
              && deliverableType !== DELIVERABLE_TYPE_INSTAGRAM_STORY && (
                <DeliverableInsights
                  isForBrand={isForBrand}
                  isEditMode={isEditMode}
                  deliverable={deliverable}
                  onEditMode={this.setEditMode}
                  attachments={attachments.filter(attachment => attachment.meta?.type === DELIVERABLE_ATTACHMENT_TYPE_HIGHRES)}
                  onUpdate={tempAttachments => {
                    this.handleUpdateAttachments(tempAttachments, DELIVERABLE_ATTACHMENT_TYPE_HIGHRES);
                  }}
                  onDeleteAttachments={this.handleDeleteAttachments}
                  onReplaceAttachment={(replacement, current) => {
                    this.handleReplaceAttachment(replacement, current, DELIVERABLE_ATTACHMENT_TYPE_HIGHRES);
                  }}
                  mode={DELIVERABLE_ATTACHMENT_TYPE_HIGHRES}
                />
              )
            }
            {
              !hasFBData && requiresAnalytics && requiresHighResImages
              && deliverableType === DELIVERABLE_TYPE_INSTAGRAM_STORY
              && !this.isStorySnippetsWidgetAvailable() && (
                <StorySnippets
                  type="story-snippets-separated-from-analytics"
                  props={{
                    deliverable,
                    isEditMode,
                    analyticsAttachments: attachments.filter(
                      attachment => attachment.meta?.type === DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS
                    ),
                    highresAttachments: attachments.filter(
                      attachment => attachment.meta?.type === DELIVERABLE_ATTACHMENT_TYPE_HIGHRES
                    ),
                    isForBrand: !!isForBrand,
                    onEditMode: this.setEditMode,
                    onAnalyticsAttachmentReplace: (replacement, current) => {
                      this.handleReplaceAttachment(
                        replacement, current, DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS
                      );
                    },
                    onAnalyticsAttachmentsDelete: this.handleDeleteAttachments,
                    onAnalyticsAttachmentsUpdate: tempAttachments => {
                      this.handleUpdateAttachments(
                        tempAttachments,
                        DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS
                      );
                    },
                    onHighresAttachmentReplace: (replacement, current) => {
                      this.handleReplaceAttachment(
                        replacement, current, DELIVERABLE_ATTACHMENT_TYPE_HIGHRES
                      );
                    },
                    onHighresAttachmentsDelete: this.handleDeleteAttachments,
                    onHighresAttachmentsUpdate: tempAttachments => {
                      this.handleUpdateAttachments(
                        tempAttachments,
                        DELIVERABLE_ATTACHMENT_TYPE_HIGHRES
                      );
                    }
                  }}
                />
              )
            }
            {
              !hasFBData && requiresAnalytics && requiresHighResImages
              && deliverableType === DELIVERABLE_TYPE_INSTAGRAM_STORY
              && this.isStorySnippetsWidgetAvailable() && (
                <StorySnippets
                  key={this.getStorySnippetsKey()}
                  type="story-snippets-linked-with-analytics"
                  props={{
                    deliverable,
                    externallyDeletedSnippetAttachments,
                    lastValidateAt: validateStorySnippetsAt,
                    orgType: isForBrand ? 'brand' : 'influencer',
                    campaignId: subcampaign.campaignId,
                    canEdit: isEditMode,
                    onAttachmentsUpdate: this.handleUpdateAttachments,
                    onAttachmentsDelete: this.handleDeleteAttachments,
                    onAttachmentReplace: this.handleReplaceAttachment,
                    onStorySnippetEdit: this.setEditMode
                  }}
                />
              )
            }
            {
              deliverableType === DELIVERABLE_TYPE_INSTAGRAM_STORY && requiresHighResImages
              && hasFBData && (
                <React.Fragment>
                  <DeliverableSubHeader
                    title={__t(`${msgKey}.select_story_snippets.title`)}
                    instruction={
                      storySnippets.length === 0
                        ? __t(`${msgKey}.select_story_snippets.instruction`)
                        : undefined
                    }
                    directon="column"
                  />
                  {storySnippets.length > 0 ? (
                    <div>
                      <StorySnippetsList
                        snippets={storySnippets}
                        canDelete={isEditMode}
                        onRemoveSnippet={snippetCode => this.setState({
                          storySnippets: storySnippets.filter(s => s.code !== snippetCode),
                          isModified: true
                        })}
                      />
                    </div>
                  ) : null}
                  {isEditMode && !isNil(campaignStartDate) ? (
                    <SelectStorySnippets
                      campaignStartDate={campaignStartDate}
                      text={
                        storySnippets.length > 0
                          ? __t(`${msgKey}.select_story_snippets.change_snippets_button`)
                          : undefined
                      }
                      deliverableId={deliverable.id}
                      selectedStorySnippets={storySnippets.map(s => s.code)}
                      saveChangesOnClose={false}
                      onSelect={snippets => this.setState({
                        storySnippets: snippets,
                        isModified: true
                      })}
                    />
                  ) : null}
                </React.Fragment>
              )
            }
          </div>
          {
            showConnectYourInstagram ? (
              <div className="deliverable-insights-tab__connect-ig">
                <ConnectYourInstagramChannel
                  onSuccessfulLogin={dispatchFetchCurrentUserAccount}
                  onLogout={dispatchFetchCurrentUserAccount}
                />
              </div>
            ) : null
          }
        </div>
        <div className="deliverable-insights-tab__section deliverable-insights-tab__actions">
          <DeliverableTabFooter
            onDownloadAttachments={this.handleDownloadAttachments}
            deliverable={deliverable}
            isEditMode={isEditMode}
            onEditModeToggle={this.setEditMode}
            attachments={attachments
              .filter(attachment => [DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS, DELIVERABLE_ATTACHMENT_TYPE_HIGHRES].includes(attachment.meta?.type))}
            isForBrand={isForBrand}
            isModified={isModified}
            onSave={this.handleSave}
            onDeleteAttachments={this.handleDeleteAttachments}
            isInsights
            onCancelChanges={this.handleCancelChanges}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (
  {
    account,
    organizations,
    subcampaign,
    campaignSubcampaigns,
    runtimeConfig
  }
): StateProps => {
  const organization: undefined | Organization = account.currentAccount.organizations
    .find(o => o.id === organizations.currentOrganizationId);

  return {
    isStorySnippetFeatureEnabled: !!runtimeConfig.config.isStorySnippetFeatureEnabled,
    hasActiveFbConnection: id => {
      if (isNil(organization)) {
        return false;
      }

      let profile: undefined | InfluencerProfile = (organization.influencerProfiles ?? [])
        .find(p => p.id === id);

      if (profile === undefined) {
        profile = subcampaign.data?.creatorProfile;
      }

      if (profile === undefined) {
        profile = campaignSubcampaigns.list.find(p => p.creatorProfile.id === id)?.creatorProfile;
      }

      return profile?.hasActiveFbConnection ?? false;
    }
  };
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchCurrentUserAccount: () => dispatch(reFetchCurrentAccountBasedOnSelectedOrg()),
  dispatchShowConfirmationModal: (params) => dispatch(showConfirmModal(params)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal()),
  dispatchUpdateInsightsData: (offerId: number,
    deliverableId: number,
    addAttachments: Array<DeliverableAttachment>,
    deleteAttachments: Array<number>,
    postUrl: string | null,
    isUrlChanged: boolean) =>
    dispatch(updateInsightsData(offerId, deliverableId, addAttachments, deleteAttachments, postUrl, isUrlChanged)),
  dispatchUpdateStoryInsightsData: (
    offerId: number,
    deliverableId: number,
    addAttachments: Array<DeliverableAttachment>,
    deleteAttachments: Array<number>,
    linkedAttachments: Array<LinkedDeliverableAttachment>,
    updateAttachments: Array<DeliverableAttachmentForUpdate>
  ) => dispatch(updateStoryInsightsData(
    offerId,
    deliverableId,
    addAttachments,
    deleteAttachments,
    linkedAttachments,
    updateAttachments
  )),
  dispatchDownloadStoryDeliverableAttachments: (
    campaignId,
    deliverable,
    creatorProfile
  ) => dispatch(downloadStoryDeliverableAttachments(campaignId, deliverable, creatorProfile)),
  dispatchDownloadAttachments: (
    campaignId: number,
    creatorProfile: SubcampaignInfluencerProfile | undefined | null,
    deliverable: Deliverable,
    attachmentType?: string | null,
    approvedOnly?: boolean | null) =>
    dispatch(downloadDeliverableAttachments(campaignId, creatorProfile, deliverable, attachmentType, approvedOnly)),
  dipatchUpdateDeliverable: (
    offerId: number,
    deliverable: Deliverable,
    post?: DeliverablePost,
    isContentPage?: boolean) => (
    dispatch(isContentPage ?
      updateDeliverableInsights(deliverable, post) :
      updateInsights(offerId, deliverable, post))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(DeliverableInsightsTab);
